import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatError, setSessionToken, clearSessionToken } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // null = checking, false = unauth, object = user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // skip /me check if we're processing google session
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Persist the token so users on Safari / Brave / hardened Chrome
      // (third-party cookies blocked) don't get bounced back to /login
      // by the AuthProvider's next /auth/me call. See lib/api.js.
      if (data?.session_token) setSessionToken(data.session_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatError(e) };
    }
  }, []);

  const register = useCallback(async (email, password, name, phone) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name, phone });
      if (data?.session_token) setSessionToken(data.session_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatError(e) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network/session already gone is OK — proceed with local logout.
      console.warn("Logout API failed (continuing with local clear):", err);
    }
    clearSessionToken();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: checkAuth }),
    [user, loading, login, register, logout, checkAuth]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
